import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Check, X, ImageUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, fetchBlobUrl, formatErr, rupiah } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Common";
import { HdDeliveryDialog } from "@/components/HdDeliveryDialog";

export default function PhotographerOrders() {
  const [orders, setOrders] = useState(null);
  const [proof, setProof] = useState(null);
  const [hdOrder, setHdOrder] = useState(null);
  const markHd = (pid) => {
    const fix = (o) => {
      const items = o.items.map((i) => (i.photo_id === pid ? { ...i, hd_ready: true } : i));
      return { ...o, items, hd_count: items.filter((i) => i.hd_ready).length };
    };
    setOrders((prev) => prev.map((o) => (o.items.some((i) => i.photo_id === pid) ? fix(o) : o)));
    setHdOrder((o) => (o ? fix(o) : o));
  };
  useEffect(() => { api.get("/photographer/orders").then((r) => setOrders(r.data)); }, []);
  const update = (o) => setOrders((prev) => prev.map((x) => (x.id === o.id ? o : x)));

  const viewProof = async (o) => {
    try {
      setProof({ order: o, url: await fetchBlobUrl(`/orders/${o.id}/proof`) });
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const act = async (o, action) => {
    try {
      const body = action === "reject" ? { note: window.prompt("Alasan penolakan:", "Bukti pembayaran tidak valid") || "" } : {};
      update((await api.post(`/photographer/orders/${o.id}/${action}`, body)).data);
      toast.success(action === "approve" ? "Pembayaran dikonfirmasi" : "Pesanan ditolak");
      setProof(null);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <PageHeader label="Konfirmasi pembayaran" title="Pesanan Masuk" />
      {orders === null ? <p className="text-zinc-500">Memuat...</p> : (
        <div className="rounded-2xl bg-white border border-zinc-200 overflow-hidden">
          <Table data-testid="pg-orders-table">
            <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Member</TableHead><TableHead>Foto</TableHead><TableHead>Total</TableHead><TableHead>Bersih</TableHead><TableHead>Status</TableHead><TableHead>File HD</TableHead><TableHead>Pencairan</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.id} data-testid={`pg-order-row-${o.id}`}>
                  <TableCell className="font-mono-x text-sm">
                    {o.code}
                    {o.hd_overdue && <span className="ml-2 rounded-md bg-rose-100 text-rose-700 px-1.5 py-0.5 text-[10px] font-bold font-sans" data-testid={`pg-order-overdue-${o.id}`}>Terlambat kirim HD</span>}
                  </TableCell>
                  <TableCell><p className="font-medium">{o.member.name}</p><p className="text-xs text-zinc-500">{o.member.email}</p></TableCell>
                  <TableCell>{o.items.length}</TableCell>
                  <TableCell className="font-mono-x">{rupiah(o.total)}</TableCell>
                  <TableCell className="font-mono-x text-emerald-700" data-testid={`pg-order-net-${o.id}`}>{rupiah(o.net ?? o.total)}<p className="text-[11px] text-zinc-400">komisi {o.commission_rate ?? 0}%</p></TableCell>
                  <TableCell><StatusBadge status={o.status} testId={`pg-order-status-${o.id}`} /></TableCell>
                  <TableCell data-testid={`pg-order-hd-${o.id}`}>
                    {o.status !== "paid" ? <span className="text-zinc-400">-</span> : (
                      <Button size="sm" variant="outline" className={`rounded-lg ${o.hd_count === o.items.length ? "text-emerald-700" : "text-amber-700 border-amber-300"}`} onClick={() => setHdOrder(o)} data-testid={`pg-order-send-hd-${o.id}`}>
                        <ImageUp className="h-4 w-4 mr-1" />{o.hd_count}/{o.items.length} HD
                      </Button>
                    )}
                  </TableCell>
                  <TableCell data-testid={`pg-order-payout-${o.id}`}>
                    {o.status !== "paid" ? <span className="text-zinc-400">-</span> : o.payout_status === "paid"
                      ? <span className="text-xs font-semibold text-emerald-700">Sudah dicairkan</span>
                      : <span className="text-xs font-semibold text-amber-700">Belum dicairkan</span>}
                  </TableCell>
                  <TableCell className="text-right space-x-1 whitespace-nowrap">
                    {o.has_proof && <Button size="sm" variant="outline" className="rounded-lg" onClick={() => viewProof(o)} data-testid={`pg-order-view-proof-${o.id}`}><Eye className="h-4 w-4" /></Button>}
                    {o.status === "waiting_confirmation" && (
                      <>
                        <Button size="sm" className="rounded-lg bg-emerald-600 hover:bg-emerald-700" onClick={() => act(o, "approve")} data-testid={`pg-order-approve-${o.id}`}><Check className="h-4 w-4 mr-1" />Setuju</Button>
                        <Button size="sm" variant="outline" className="rounded-lg text-rose-600" onClick={() => act(o, "reject")} data-testid={`pg-order-reject-${o.id}`}><X className="h-4 w-4 mr-1" />Tolak</Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {orders.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-zinc-500 py-12" data-testid="pg-orders-empty">Belum ada pesanan.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
      <HdDeliveryDialog order={hdOrder} onClose={() => setHdOrder(null)} onSent={markHd} />
      <Dialog open={!!proof} onOpenChange={(v) => !v && setProof(null)}>
        <DialogContent className="rounded-2xl max-w-2xl" data-testid="proof-dialog">
          <DialogHeader><DialogTitle>Bukti transfer · {proof?.order.code}</DialogTitle></DialogHeader>
          {proof && <img src={proof.url} alt="Bukti transfer" className="w-full max-h-[60vh] object-contain rounded-xl bg-zinc-50" data-testid="proof-image" />}
          {proof?.order.status === "waiting_confirmation" && (
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-xl text-rose-600" onClick={() => act(proof.order, "reject")} data-testid="proof-reject-button">Tolak</Button>
              <Button className="rounded-xl bg-emerald-600 hover:bg-emerald-700" onClick={() => act(proof.order, "approve")} data-testid="proof-approve-button">Setuju</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
